import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { PiFileTextLight, PiCheckCircleLight, PiHandshakeLight } from 'react-icons/pi';
import { generateEngagementLetterSSE, updateEngagementLetter, markAsFinal, respondToEngagementLetter, fetchEngagementLetterVersions, uploadFilesForLetter } from '../../store/actions/Drafts/EngagementLetterActions';
import { RootState, useAppDispatch } from '../../store';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import Button from '../shared/TailwindComponents/Button';
import DynamicEditor from '../Cases/Editor/DynamicEditor';
import FileUploader from '../shared/TailwindComponents/FileUploader';
import Textarea from '../shared/TailwindComponents/Textarea';
import MiniLoadingToCheckmark from '../shared/animations/MiniLoadingToCheckmark';
import { showModal } from '../../store/actions/modalActions';

interface EngagementTermsProps {
  clientId: string;
}

interface EngagementLetterVersion {
  Version: number;
  Content: string;
  HTMLContent: string;
  Status: string;
  ClientResponse: string;
  ClientComment: string;
}

const EngagementTerms: React.FC<EngagementTermsProps> = ({ clientId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [letterId, setLetterId] = useState<string | null>(null);
  const [versions, setVersions] = useState<EngagementLetterVersion[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [isFinalized, setIsFinalized] = useState(false);
  const [clientResponse, setClientResponse] = useState<string | null>(null);
  const [specifications, setSpecifications] = useState<string>('');
  const [files, setFiles] = useState<File[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progressSteps, setProgressSteps] = useState<any[]>([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const userType = useSelector((state: RootState) => state.user.userInfo.UserTypeName);
  const { progress, letterComplete, letterId: generatedLetterId } = useSelector((state: RootState) => state.engagementLetter);
  const isLawyer = ['LawFirmAdmin', 'LawFirmEmployee', 'IndependentLawyer'].includes(userType);
  const selectedVersionData = versions.find(v => v.Version === selectedVersion);
  const isReadOnly = !isLawyer || (isFinalized || clientResponse === 'Accepted');

  useEffect(() => {
    fetchLatestVersions();
  }, [clientId, dispatch]);

  const fetchLatestVersions = async () => {
    const fetchedVersions = await dispatch(fetchEngagementLetterVersions(clientId));
    if (fetchedVersions && fetchedVersions.length > 0) {
      setVersions(fetchedVersions);
      const latestVersion = fetchedVersions[0];
      setSelectedVersion(latestVersion.Version);
      setIsFinalized(latestVersion.Status === 'Final');
      setClientResponse(latestVersion.ClientResponse);
      if (editorRef.current) editorRef.current.innerHTML = latestVersion.HTMLContent;
    }
  };

  useEffect(() => {
    if (letterComplete && generatedLetterId) {
      setLetterId(generatedLetterId);
      setIsGenerating(false);
      fetchLatestVersions(); 
    }
  }, [letterComplete, generatedLetterId]);

  useEffect(() => {
    if (progress) {
      setProgressSteps(progress);
    }
  }, [progress]);

  const handleGenerateLetter = async () => {
    setIsGenerating(true);
    setProgressSteps([]);
    try {
      let fileReferences: string[] = [];
      if (files.length > 0) {
        fileReferences = await dispatch(uploadFilesForLetter(files));
      }
      await dispatch(generateEngagementLetterSSE(clientId, specifications, fileReferences as any[]));
    } catch (error) {
      if(!error) return
      dispatch(showFeedbackModal({ modalType: 'error', message: t('Failed to generate engagement letter'), showModal: true, duration: 3000 }));
      setIsGenerating(false);
    }
  };

  const handleSave = async (content: string) => {
    if (selectedVersion !== null && letterId) {
      await dispatch(updateEngagementLetter(letterId, selectedVersion, content));
      await fetchLatestVersions(); 
      dispatch(showFeedbackModal({ modalType: 'success', message: t('Engagement letter saved successfully'), showModal: true, duration: 3000 }));
    }
  };

  const handleMarkAsFinal = () => {
    if (selectedVersion !== null) {
      dispatch(
        showModal({
          type: 'signFinalEngagement',
          message: '',
          onConfirm: async (signatureFile: File) => {
            await dispatch(markAsFinal(clientId, selectedVersion, signatureFile));
            await fetchLatestVersions(); 
            dispatch(showFeedbackModal({ modalType: 'success', message: t('Engagement letter marked as final'), showModal: true, duration: 3000 }));
          },
          showModal: true,
        })
      );
    }
  };

  const handleRespond = async (response: 'Accepted' | 'Declined', comment?: string) => {
    if (selectedVersion !== null && letterId) {
      await dispatch(respondToEngagementLetter(letterId, selectedVersion, response, comment));
      await fetchLatestVersions();
      dispatch(showFeedbackModal({ modalType: 'success', message: t(`Engagement letter ${response.toLowerCase()}`), showModal: true, duration: 3000 }));
    }
  };

  const handleVersionChange = (value: string | string[]) => {
    const versionNum = Number.parseInt(value as string, 10);
    const version = versions.find(v => v.Version === versionNum);
    if (version) {
      setSelectedVersion(versionNum);
      setIsFinalized(version.Status === 'Final');
      setClientResponse(version.ClientResponse);
      if (editorRef.current) editorRef.current.innerHTML = version.HTMLContent;
    }
  };

  const versionOptions = versions.map(v => ({
    value: v.Version.toString(),
    label: `Version ${v.Version} ${v.Status === 'Final' ? '(Final)' : '(Draft)'}`,
    icon: v.Status === 'Final' ? <PiCheckCircleLight className="text-green-500" /> : null,
    active: true,
  }));

  if (isGenerating) {
    return (
      <div className="px-6 flex justify-center items-center animate-fade-in h-[calc(100vh-150px)]">
        <div className="w-full max-w-md p-6 flex flex-col bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <PiHandshakeLight className="h-6 w-6 text-primary-500" />
            {t('Generating Engagement Letter...')}
          </h3>
          <div className="flex-grow border border-gray-300 dark:border-gray-600 rounded-lg p-4 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
            {progressSteps.map((step, index) => (
              <div
                key={index}
                className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                {step.status === 'In Progress' ? (
                  <MiniLoadingToCheckmark isLoading={true} spinnerSize={20} checkSize={20} />
                ) : step.status === 'Completed' ? (
                  <PiCheckCircleLight className="h-5 w-5 text-green-500" />
                ) : (
                  <PiFileTextLight className="h-5 w-5 text-red-500" />
                )}
                <div>
                  <p>{step.step}: {step.status}</p>
                  {step.messages.length > 0 && (
                    <ul className="ml-4 list-disc">
                      {step.messages.map((msg: string, msgIndex: number) => (
                        <li key={msgIndex}>{msg}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium inline-block py-1 px-3 rounded-full bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                {Math.round((progressSteps.filter(p => p.status === 'Completed').length / progressSteps.length) * 100)}%
              </span>
            </div>
            <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
              <div
                style={{ width: `${(progressSteps.filter(p => p.status === 'Completed').length / progressSteps.length) * 100}%` }}
                className="h-full bg-gradient-to-r from-primary-500 to-primary-900 transition-all duration-700 ease-in-out"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 overflow-y-auto custom-scrollbar-main">
      {versions.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg max-w-lg w-full mx-auto mt-6">
          <h3 className="text-xl font-semibold">{t('Generate Engagement Letter')}</h3>
          <p className="mb-4 mt-1 text-xs text-gray-600">{t('Optionally add specifications to your engagement letter if needed. You can also generate without any specifications and PONS will use the data available.')}</p>
          <Textarea
            value={specifications}
            onChange={(e) => setSpecifications(e.target.value)}
            placeholder={t('Enter specifications for the engagement letter')}
            className="w-full p-2 mb-4 border rounded dark:bg-gray-700 dark:text-gray-100"
            id="engagementletter"
            label=""
          />
          <FileUploader files={files} onFilesChange={setFiles} />
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="primary" size="small" onClick={handleGenerateLetter} disabled={isGenerating}>
              {isGenerating ? t('Generating...') : t('Generate')}
            </Button>
          </div>
        </div>
      ) : (
        <DynamicEditor
          editorRef={editorRef}
          contentType={t('Engagement Letter')}
          matterId={clientId}
          content={selectedVersionData?.HTMLContent || ''}
          isFullscreen={isFullscreen}
          setIsFullscreen={setIsFullscreen}
          onSave={handleSave}
          handleMarkAsFinal={isLawyer && !isFinalized ? handleMarkAsFinal : undefined}
          selectedVersionStatus={isFinalized ? 'Final' : 'Draft'}
          versionOptions={versionOptions}
          handleVersionChange={handleVersionChange}
          selectedVersion={selectedVersion}
          isReadOnly={isReadOnly}
        />
      )}
      {!isLawyer && isFinalized && clientResponse === 'Pending' && (
        <div className="mt-4 flex gap-2 justify-center">
          <Button variant="primary" size="small" onClick={() => handleRespond('Accepted')}>
            {t('Accept')}
          </Button>
          <Button
            variant="secondary"
            size="small"
            onClick={() => {
              const comment = prompt(t('Please provide a reason for declining'));
              if (comment) handleRespond('Declined', comment);
            }}
          >
            {t('Decline')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default EngagementTerms;
